import { Copy, Pencil, Power, Trash2 } from "lucide-react";
import { DsBadge, DsButton, DsIcon } from "../../../design-system";
import { ADMIN_RBAC_CATALOG } from "../../../rbac/catalog";
import { LEVELS_WITH_ITEMS, countAssignableEmployees, dataLevelLabel } from "./roleAccessUtils";

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <dt className="text-xs text-[hsl(var(--ds-muted-fg))]">{label}</dt>
      <dd className="min-w-0 text-right text-sm font-medium text-[hsl(var(--ds-fg))]">{children}</dd>
    </div>
  );
}

function ActionButton({ icon, label, blocked, onClick, variant = "outline" }) {
  return (
    <DsButton
      variant={variant}
      size="sm"
      className="w-full justify-start"
      disabled={!!blocked}
      title={blocked || undefined}
      onClick={onClick}
    >
      <DsIcon icon={icon} size="sm" />
      {label}
    </DsButton>
  );
}

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export default function RoleSummaryPanel({
  role,
  isSystem,
  usesAccess,
  stats,
  dataAccess,
  branches,
  departments,
  blockers = {},
  /** When false, only the details list is shown (used by the Info menu action). */
  showActions = true,
  className = "",
  onEdit,
  onCopy,
  onToggleActive,
  onDelete,
}) {
  if (!role) {
    return (
      <aside className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-5 shadow-[var(--ds-shadow-card)]">
        <p className="ds-body text-[hsl(var(--ds-muted-fg))]">No role selected.</p>
      </aside>
    );
  }

  const items = LEVELS_WITH_ITEMS.has(dataAccess?.level)
    ? (dataAccess.level === "department" ? departments : branches) || []
    : [];
  const itemNames = items
    .filter((it) => (dataAccess?.selectedItems || []).map(Number).includes(Number(it.id)))
    .map((it) => it.name);

  const assignment = isSystem
    ? "Default role"
    : role.assignmentType === "all"
      ? "All employees"
      : role.assignmentType === "filter"
        ? `Filtered (${countAssignableEmployees(role.filterData)} selected)`
        : "Not set";

  const delegable = (role.delegableModules || [])
    .map((k) => ADMIN_RBAC_CATALOG.find((m) => m.key === k)?.label)
    .filter(Boolean);

  return (
    <aside
      aria-label="Role summary"
      className={
        className ||
        "flex min-h-0 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
      }
    >
      <div className="shrink-0 border-b border-[hsl(var(--ds-border))] p-4">
        <h3 className="ds-h4 break-words text-[hsl(var(--ds-fg))]">{role.name}</h3>
        <p className="mt-0.5 font-mono text-[11px] text-[hsl(var(--ds-muted-fg))]">{role.code}</p>
        {role.description ? (
          <p className="ds-caption mt-2 text-[hsl(var(--ds-muted-fg))]">{role.description}</p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        <dl className="divide-y divide-[hsl(var(--ds-border))]">
          <Row label="Type">
            {isSystem ? "System" : "Custom"} · {usesAccess ? "Access Based" : "Action Based"}
          </Row>
          <Row label="Status">
            <DsBadge tone={role.active ? "success" : "warning"}>{role.active ? "Active" : "Inactive"}</DsBadge>
          </Row>
          <Row label="Assigned users">{role.assignedUserCount ?? 0}</Row>
          <Row label="Assignment">{assignment}</Row>
          {usesAccess ? (
            <>
              <Row label="Enabled modules">
                {stats.modulesOn} / {stats.modulesTotal}
              </Row>
              <Row label="Total permissions">{stats.permissions}</Row>
              <Row label="Data access">
                {dataLevelLabel(dataAccess?.level)}
                {itemNames.length ? (
                  <span className="block text-[11px] font-normal text-[hsl(var(--ds-muted-fg))]">
                    {itemNames.join(", ")}
                  </span>
                ) : null}
              </Row>
            </>
          ) : null}
          {delegable.length ? (
            <Row label="Can grant modules">
              <span className="text-xs font-normal">{delegable.join(", ")}</span>
            </Row>
          ) : null}
          <Row label="Last updated">{formatDate(role.updatedAt || role.createdAt)}</Row>
        </dl>
      </div>

      {showActions ? (
        <div className="shrink-0 space-y-2 border-t border-[hsl(var(--ds-border))] p-4">
          <ActionButton icon={Pencil} label="Edit role" variant="primary" blocked={blockers.edit} onClick={onEdit} />
          {usesAccess && !isSystem ? (
            <ActionButton icon={Copy} label="Copy access to new role" blocked={blockers.copy} onClick={onCopy} />
          ) : null}
          {!isSystem ? (
            <ActionButton
              icon={Power}
              label={role.active ? "Deactivate" : "Reactivate"}
              blocked={blockers.toggle}
              onClick={onToggleActive}
            />
          ) : null}
          {!isSystem && role.deletable !== false ? (
            <ActionButton icon={Trash2} label="Delete role" variant="danger" blocked={blockers.delete} onClick={onDelete} />
          ) : null}
          {Object.values(blockers).find(Boolean) ? (
            <p className="text-[11px] leading-snug text-[hsl(var(--ds-muted-fg))]">
              {Object.values(blockers).find(Boolean)}
            </p>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}
